import { FC, ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { usePortal } from "../context/PortalContext";
import { useUserAuth } from "../context/AuthContext";

interface ProtectedRouteProps {
  // If you have different conditions for different steps,
  // you can pass props such as `requireFullQuoteCompleted?: boolean`
  requireFullQuote?: boolean;
  requireFactfindCompleted?: boolean;
  requireAgreedtoProceed?: boolean;
  redirectTo?: string;
  children: ReactElement;
}

const ProtectedRoute: FC<ProtectedRouteProps> = ({
  requireFullQuote,
  requireFactfindCompleted,
  requireAgreedtoProceed,
  redirectTo,
  children,
}) => {
  const { user, loading } = useUserAuth();
  const { clientData } = usePortal();

  // If we're still checking auth state, you might render a loader or return null
  if (loading) {
    return null;
  }

  // If there's no user, definitely not allowed - send them to registration
  if (!user) {
    return <Navigate to="/auth/registration" replace />;
  }

  // If you need certain steps done before allowing them in:
  if (requireFullQuote) {
    const hasFullQuote = clientData?.annuity?.fullQuoteCompleted === "true";
    if (!hasFullQuote) {
      // If they haven't completed full quote, redirect them
      return <Navigate to="/Pilot" replace />;
    }
  }

  if (requireFactfindCompleted) {
    const hasFactfindCompleted =
      clientData?.annuity?.factfindCompleted === "true";
    if (!hasFactfindCompleted) {
      // If they haven't completed factfind, redirect
      return <Navigate to="/Pilot" replace />;
    }
  }

  if (requireAgreedtoProceed) {
    const hasAgreedToProceed = clientData?.annuity?.milestones?.some(
      (milestone) => milestone?.milestoneId === "100197" && milestone?.completed === true
    );

    if (!hasAgreedToProceed) {
      return <Navigate to={redirectTo ?? "/overview"} replace />;
    }
  }

  // If we got here, conditions are all met
  return children;
};

export default ProtectedRoute;
